import {
  useCallback,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type PointerEvent,
} from "react";
import {
  FOUNDATION_INDEXES,
  TABLEAU_INDEXES,
  destKey,
  legalDestinations,
  peekCards,
  sameSource,
  visibleWaste,
  type PasjansCard,
  type PasjansDest,
  type PasjansSource,
  type PasjansState,
  type TableauIndex,
} from "@ziolo/shared";

const DRAG_THRESHOLD_PX = 10;
const STOCK_BACKS = 8;
const WASTE_THICKNESS = 3;

interface BoardProps {
  state: PasjansState;
  onStock: () => void;
  onMove: (source: PasjansSource, dest: PasjansDest) => boolean;
  onAutoFoundation: (source: PasjansSource) => boolean;
}

interface DragSession {
  pointerId: number;
  source: PasjansSource;
  cards: PasjansCard[];
  offsetX: number;
  offsetY: number;
  originX: number;
  originY: number;
  dragging: boolean;
}

export function PasjansBoard({ state, onStock, onMove, onAutoFoundation }: BoardProps) {
  const [selected, setSelected] = useState<PasjansSource | null>(null);
  const [drag, setDrag] = useState<DragSession | null>(null);
  const sessionRef = useRef<DragSession | null>(null);
  const ghostRef = useRef<HTMLDivElement>(null);

  const activeSource = drag?.source ?? selected;
  const validKeys = useMemo(() => {
    if (!activeSource) return new Set<string>();
    return new Set(legalDestinations(state, activeSource).map(destKey));
  }, [activeSource, state]);
  const validKeysRef = useRef(validKeys);
  validKeysRef.current = validKeys;

  const liftedIds = useMemo(() => {
    if (!drag?.dragging) return new Set<string>();
    return new Set(drag.cards.map((card) => card.id));
  }, [drag]);

  const clearPointer = useCallback(() => {
    sessionRef.current = null;
    setDrag(null);
    clearDropHover();
  }, []);

  const handleTap = useCallback(
    (hit: { source?: PasjansSource | undefined; dest?: PasjansDest | undefined }) => {
      if (selected) {
        if (hit.dest && onMove(selected, hit.dest)) {
          setSelected(null);
          return;
        }
        if (hit.source && sameSource(hit.source, selected)) {
          if (!onAutoFoundation(selected)) setSelected(null);
          return;
        }
      }
      setSelected(hit.source ?? null);
    },
    [onAutoFoundation, onMove, selected],
  );

  const onCardPointerDown = useCallback(
    (event: PointerEvent<HTMLDivElement>, source: PasjansSource) => {
      if (event.button !== 0 && event.pointerType === "mouse") return;
      const cards = peekCards(state, source);
      if (cards.length === 0) return;

      event.preventDefault();
      event.stopPropagation();
      event.currentTarget.setPointerCapture(event.pointerId);
      const rect = event.currentTarget.getBoundingClientRect();
      const session: DragSession = {
        pointerId: event.pointerId,
        source,
        cards,
        offsetX: event.clientX - rect.left,
        offsetY: event.clientY - rect.top,
        originX: event.clientX,
        originY: event.clientY,
        dragging: false,
      };
      sessionRef.current = session;
    },
    [state],
  );

  const onCardPointerMove = useCallback((event: PointerEvent<HTMLDivElement>) => {
    const session = sessionRef.current;
    if (!session || session.pointerId !== event.pointerId) return;

    const dx = event.clientX - session.originX;
    const dy = event.clientY - session.originY;
    if (!session.dragging && dx * dx + dy * dy >= DRAG_THRESHOLD_PX * DRAG_THRESHOLD_PX) {
      session.dragging = true;
      setSelected(null);
      setDrag({ ...session });
    }

    if (session.dragging) {
      updateDropHover(destFromPoint(event.clientX, event.clientY), validKeysRef.current);
    }

    if (ghostRef.current) {
      ghostRef.current.style.transform = `translate(${event.clientX - session.offsetX}px, ${event.clientY - session.offsetY}px)`;
    }
  }, []);

  const onCardPointerUp = useCallback(
    (event: PointerEvent<HTMLDivElement>) => {
      const session = sessionRef.current;
      if (!session || session.pointerId !== event.pointerId) return;

      event.stopPropagation();
      try {
        event.currentTarget.releasePointerCapture(event.pointerId);
      } catch {
        // Capture can already be released on pointercancel.
      }

      if (session.dragging) {
        const dest = destFromPoint(event.clientX, event.clientY);
        if (dest) onMove(session.source, dest);
        clearPointer();
        return;
      }

      clearPointer();
      handleTap({
        source: session.source,
        dest: destOfSource(session.source),
      });
    },
    [clearPointer, handleTap, onMove],
  );

  const onCardPointerCancel = useCallback(
    (event: PointerEvent<HTMLDivElement>) => {
      const session = sessionRef.current;
      if (!session || session.pointerId !== event.pointerId) return;
      event.stopPropagation();
      clearPointer();
    },
    [clearPointer],
  );

  const onPilePointerUp = useCallback(
    (event: PointerEvent<HTMLDivElement>, dest: PasjansDest) => {
      if (sessionRef.current) return;
      if (event.button !== 0 && event.pointerType === "mouse") return;
      if (event.target instanceof Element && event.target.closest(".pas-card--live")) {
        return;
      }
      handleTap({ dest });
    },
    [handleTap],
  );

  const waste = visibleWaste(state);
  const wasteThickness = Math.min(
    Math.max(0, state.waste.length - waste.length),
    WASTE_THICKNESS,
  );

  return (
    <div className="pas-board">
      <button
        type="button"
        className={`pas-pile pas-pile--stock${state.stock.length === 0 ? " pas-pile--empty" : ""}`}
        aria-label={
          state.stock.length > 0
            ? "Dobierz karty"
            : state.waste.length > 0
              ? "Przełóż stos z powrotem"
              : "Pusty stos"
        }
        onClick={onStock}
      >
        {stackBacks(Math.min(state.stock.length, STOCK_BACKS)).map((i) => (
          <div
            key={i}
            className="pas-card pas-card--back pas-card--stacked"
            style={{ top: i * 2, left: i * 2, zIndex: i }}
          />
        ))}
      </button>

      <div
        className={`pas-pile pas-pile--waste${state.waste.length === 0 ? " pas-pile--empty" : ""}`}
      >
        {stackBacks(wasteThickness).map((i) => (
          <div
            key={`waste-under-${i}`}
            className="pas-card pas-card--back pas-card--stacked pas-card--waste-under"
            style={{ top: i, left: i, zIndex: i }}
          />
        ))}
        {waste.map((card, index) => {
          const isTop = index === waste.length - 1;
          return (
            <CardFace
              key={card.id}
              card={card}
              interactive={isTop}
              lifted={liftedIds.has(card.id)}
              selected={Boolean(selected && selected.kind === "waste" && isTop)}
              style={{
                top: 0,
                left: `calc(${index} * var(--pas-fan))`,
                zIndex: 20 + index,
              }}
              onPointerDown={
                isTop ? (event) => onCardPointerDown(event, { kind: "waste" }) : undefined
              }
              onPointerMove={isTop ? onCardPointerMove : undefined}
              onPointerUp={isTop ? onCardPointerUp : undefined}
              onPointerCancel={isTop ? onCardPointerCancel : undefined}
            />
          );
        })}
      </div>

      <div className="pas-pile pas-pile--gap" aria-hidden="true" />

      {FOUNDATION_INDEXES.map((index) => {
        const pile = state.foundations[index];
        const top = pile[pile.length - 1];
        const dest: PasjansDest = { kind: "foundation", index };
        return (
          <div
            key={`foundation-${index}`}
            className={`pas-pile pas-pile--foundation${validKeys.has(destKey(dest)) ? " pas-pile--valid" : ""}`}
            style={{ "--pas-stack": "1" } as CSSProperties}
            data-pas-drop={destKey(dest)}
            onPointerUp={(event) => onPilePointerUp(event, dest)}
          >
            {top ? (
              <CardFace
                card={top}
                interactive
                lifted={liftedIds.has(top.id)}
                selected={Boolean(
                  selected && selected.kind === "foundation" && selected.index === index,
                )}
                onPointerDown={(event) =>
                  onCardPointerDown(event, { kind: "foundation", index })
                }
                onPointerMove={onCardPointerMove}
                onPointerUp={onCardPointerUp}
                onPointerCancel={onCardPointerCancel}
              />
            ) : null}
          </div>
        );
      })}

      {TABLEAU_INDEXES.map((index) => {
        const pile = state.tableaus[index];
        const dest: PasjansDest = { kind: "tableau", index };
        return (
          <div
            key={`tableau-${index}`}
            className={`pas-pile pas-pile--tableau${validKeys.has(destKey(dest)) ? " pas-pile--valid" : ""}${pile.length === 0 ? " pas-pile--empty" : ""}`}
            style={{ "--pas-stack": String(Math.max(pile.length, 1)) } as CSSProperties}
            data-pas-drop={destKey(dest)}
            onPointerUp={(event) => onPilePointerUp(event, dest)}
          >
            {pile.map((card, start) => (
              <CardFace
                key={card.id}
                card={card}
                interactive={!card.faceDown}
                lifted={liftedIds.has(card.id)}
                selected={isTableauSelected(selected, index, start)}
                style={{
                  top: `calc(${start} * var(--pas-offset))`,
                  zIndex: start + 1,
                }}
                onPointerDown={
                  card.faceDown
                    ? undefined
                    : (event) => onCardPointerDown(event, { kind: "tableau", index, start })
                }
                onPointerMove={card.faceDown ? undefined : onCardPointerMove}
                onPointerUp={card.faceDown ? undefined : onCardPointerUp}
                onPointerCancel={card.faceDown ? undefined : onCardPointerCancel}
              />
            ))}
          </div>
        );
      })}

      {drag?.dragging ? (
        <div
          ref={ghostRef}
          className="pas-ghost"
          style={{
            transform: `translate(${drag.originX - drag.offsetX}px, ${drag.originY - drag.offsetY}px)`,
          }}
        >
          {drag.cards.map((card, index) => (
            <CardFace
              key={card.id}
              card={card}
              style={{ top: `calc(${index} * var(--pas-offset))`, zIndex: index }}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}

function destOfSource(source: PasjansSource): PasjansDest | undefined {
  if (source.kind === "waste") return undefined;
  if (source.kind === "foundation") return { kind: "foundation", index: source.index };
  return { kind: "tableau", index: source.index };
}

function isTableauSelected(
  selected: PasjansSource | null,
  index: TableauIndex,
  start: number,
): boolean {
  if (!selected || selected.kind !== "tableau") return false;
  return selected.index === index && start >= selected.start;
}

function clearDropHover() {
  document.querySelectorAll(".pas-pile--drop").forEach((node) => {
    node.classList.remove("pas-pile--drop");
  });
}

function updateDropHover(dest: PasjansDest | null, valid: Set<string>) {
  clearDropHover();
  if (!dest) return;
  const key = destKey(dest);
  if (!valid.has(key)) return;
  const pile = document.querySelector(`[data-pas-drop="${key}"]`);
  pile?.classList.add("pas-pile--drop");
}

function destFromPoint(x: number, y: number): PasjansDest | null {
  const node = document.elementFromPoint(x, y);
  if (!(node instanceof Element)) return null;
  const pile = node.closest("[data-pas-drop]");
  if (!(pile instanceof HTMLElement)) return null;
  return parseDest(pile.dataset.pasDrop);
}

function parseDest(value: string | undefined): PasjansDest | null {
  if (!value) return null;
  const [kind, raw] = value.split(":");
  const index = Number(raw);
  if (kind === "foundation" && (index === 0 || index === 1 || index === 2 || index === 3)) {
    return { kind: "foundation", index };
  }
  if (
    kind === "tableau" &&
    Number.isInteger(index) &&
    index >= 0 &&
    index <= 6
  ) {
    return { kind: "tableau", index: index as TableauIndex };
  }
  return null;
}

function stackBacks(count: number): number[] {
  return Array.from({ length: count }, (_, i) => i);
}

interface CardFaceProps {
  card: PasjansCard;
  style?: CSSProperties;
  interactive?: boolean;
  lifted?: boolean;
  selected?: boolean;
  onPointerDown?: ((event: PointerEvent<HTMLDivElement>) => void) | undefined;
  onPointerMove?: ((event: PointerEvent<HTMLDivElement>) => void) | undefined;
  onPointerUp?: ((event: PointerEvent<HTMLDivElement>) => void) | undefined;
  onPointerCancel?: ((event: PointerEvent<HTMLDivElement>) => void) | undefined;
}

function CardFace({
  card,
  style,
  interactive = false,
  lifted = false,
  selected = false,
  onPointerDown,
  onPointerMove,
  onPointerUp,
  onPointerCancel,
}: CardFaceProps) {
  const classes = [
    "pas-card",
    card.faceDown ? "pas-card--back" : `pas-card--${card.color}`,
    interactive ? "pas-card--live" : "",
    lifted ? "pas-card--lifted" : "",
    selected ? "pas-card--selected" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div
      className={classes}
      style={style}
      draggable={false}
      role={interactive ? "button" : undefined}
      aria-label={interactive ? `${card.value}${card.suit}` : undefined}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerCancel}
    >
      {card.faceDown ? null : (
        <>
          <span className="pas-card__corner pas-card__corner--tl">
            <span className="pas-card__value">{card.value}</span>
            <span className="pas-card__suit">{card.suit}</span>
          </span>
          <span className="pas-card__corner pas-card__corner--br">
            <span className="pas-card__value">{card.value}</span>
            <span className="pas-card__suit">{card.suit}</span>
          </span>
        </>
      )}
    </div>
  );
}
